import * as signalR from '@microsoft/signalr';
import { Events } from './events';

export interface User {
    id: string;
    email: string;
    username: string;
}

export type JobStatus = 'Pending' | 'Processing' | 'Done' | 'Failed';

export interface RemoteScene {
    id: string;
    name: string;
    timestamp: string;
    status: JobStatus;
    progress: number;
    errorMessage: string | null;
}

class AuthService {
    private events: Events;
    private accessToken: string | null = null;
    private refreshToken: string | null = null;
    private user: User | null = null;
    // In production, nginx proxies /api and /hubs to the backend on the same origin.
    // Locally, the dev server runs on a different port than the .NET API.
    private readonly backendOrigin = window.location.hostname === 'localhost' ? 'http://localhost:5268' : '';
    private readonly apiBaseUrl = `${this.backendOrigin}/api`;
    private readonly hubUrl = `${this.backendOrigin}/hubs/progress`;
    private hubConnection: signalR.HubConnection | null = null;
    private activeProjectIds = new Set<string>();

    constructor(events: Events) {
        this.events = events;
        this.loadFromStorage();
    }

    private loadFromStorage() {
        this.accessToken = localStorage.getItem('sf_access_token');
        this.refreshToken = localStorage.getItem('sf_refresh_token');
        const savedUser = localStorage.getItem('auth_user');
        if (savedUser) {
            try {
                this.user = JSON.parse(savedUser);
            } catch {
                this.user = null;
            }
        }

        if (this.accessToken && this.user) {
            setTimeout(() => {
                this.events.fire('user.authenticated', this.user);
                this.initHub();
            }, 0);
        }
    }

    private parseJwt(token: string) {
        try {
            const base64Url = token.split('.')[1];
            const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
            const jsonPayload = decodeURIComponent(
                window.atob(base64).split('').map(c =>
                    '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
                ).join('')
            );
            return JSON.parse(jsonPayload);
        } catch {
            return null;
        }
    }

    private saveSession(accessToken: string, refreshToken: string, user: User) {
        this.accessToken = accessToken;
        this.refreshToken = refreshToken;
        this.user = user;
        localStorage.setItem('sf_access_token', accessToken);
        localStorage.setItem('sf_refresh_token', refreshToken);
        localStorage.setItem('auth_user', JSON.stringify(user));
    }

    private clearSession() {
        this.accessToken = null;
        this.refreshToken = null;
        this.user = null;
        localStorage.removeItem('sf_access_token');
        localStorage.removeItem('sf_refresh_token');
        localStorage.removeItem('auth_user');
    }

    private async doRefreshTokens(): Promise<boolean> {
        if (!this.refreshToken) return false;
        try {
            const res = await fetch(`${this.apiBaseUrl}/auth/refresh`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ refreshToken: this.refreshToken })
            });
            if (!res.ok) {
                this.clearSession();
                this.events.fire('user.loggedOut');
                return false;
            }
            const data = await res.json();
            this.accessToken = data.accessToken;
            this.refreshToken = data.refreshToken;
            localStorage.setItem('sf_access_token', data.accessToken);
            localStorage.setItem('sf_refresh_token', data.refreshToken);
            return true;
        } catch {
            return false;
        }
    }

    private async apiFetch(path: string, options: RequestInit = {}): Promise<Response> {
        const makeHeaders = () => {
            const h = new Headers(options.headers as HeadersInit);
            if (this.accessToken) h.set('Authorization', `Bearer ${this.accessToken}`);
            if (!h.has('Content-Type') && !(options.body instanceof FormData)) {
                h.set('Content-Type', 'application/json');
            }
            return h;
        };

        let res = await fetch(`${this.apiBaseUrl}${path}`, { ...options, headers: makeHeaders() });

        if (res.status === 401 && this.refreshToken) {
            const refreshed = await this.doRefreshTokens();
            if (refreshed) {
                res = await fetch(`${this.apiBaseUrl}${path}`, { ...options, headers: makeHeaders() });
            }
        }

        return res;
    }

    async login(email: string, password: string): Promise<User | null> {
        try {
            const res = await fetch(`${this.apiBaseUrl}/auth/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, password })
            });

            if (!res.ok) return null;

            const data = await res.json();
            if (!data.accessToken) return null;

            const payload = this.parseJwt(data.accessToken);
            const user: User = {
                id: payload?.sub || 'unknown',
                email: payload?.email || email,
                username: (payload?.email || email).split('@')[0]
            };

            this.saveSession(data.accessToken, data.refreshToken, user);
            this.events.fire('user.authenticated', user);
            this.initHub();
            return user;
        } catch (error) {
            console.error('Login error:', error);
            return null;
        }
    }

    logout() {
        this.hubConnection?.stop();
        this.hubConnection = null;
        this.activeProjectIds.clear();
        this.clearSession();
        this.events.fire('user.loggedOut');
    }

    private async initHub() {
        if (this.hubConnection) return;
        if (!this.accessToken) return;

        this.hubConnection = new signalR.HubConnectionBuilder()
            .withUrl(this.hubUrl, {
                accessTokenFactory: () => this.accessToken!
            })
            .withAutomaticReconnect()
            .build();

        this.hubConnection.on('ProgressUpdated', (data: { projectId: string; percent: number }) => {
            this.events.fire('remoteScene.progress', {
                id: data.projectId,
                progress: data.percent,
                status: 'Processing'
            });
        });

        this.hubConnection.on('GenerationComplete', (data: { projectId: string }) => {
            this.events.fire('remoteScene.progress', {
                id: data.projectId,
                progress: 100,
                status: 'Done'
            });
        });

        this.hubConnection.on('GenerationFailed', (data: { projectId: string; error: string }) => {
            this.events.fire('remoteScene.progress', {
                id: data.projectId,
                progress: 0,
                status: 'Failed',
                error: data.error
            });
        });

        this.hubConnection.onreconnected(async () => {
            for (const id of this.activeProjectIds) {
                await this.subscribeToProject(id);
            }
        });

        try {
            await this.hubConnection.start();
        } catch (err) {
            console.error('SignalR connection error:', err);
        }
    }

    private async subscribeToProject(projectId: string) {
        this.activeProjectIds.add(projectId);
        if (this.hubConnection?.state === signalR.HubConnectionState.Connected) {
            try {
                await this.hubConnection.invoke('SubscribeToProject', projectId);
            } catch (err) {
                console.error(`Subscribe error for ${projectId}:`, err);
            }
        }
    }

    isAuthenticated(): boolean {
        return !!this.accessToken;
    }

    getToken(): string | null {
        return this.accessToken;
    }

    getUser(): User | null {
        return this.user;
    }

    async getRemoteScenes(): Promise<RemoteScene[]> {
        try {
            const res = await this.apiFetch('/projects');
            if (!res.ok) return [];

            const projects: any[] = await res.json();
            // Backend serializes JobStatus as integer: 0=Pending, 1=Processing, 2=Done, 3=Failed
            const STATUS_MAP: Record<number, JobStatus> = { 0: 'Pending', 1: 'Processing', 2: 'Done', 3: 'Failed' };

            const scenes: RemoteScene[] = projects.map(p => ({
                id: p.id,
                name: p.name,
                timestamp: p.createdAt ?? '',
                status: (typeof p.status === 'number' ? STATUS_MAP[p.status] : p.status) ?? 'Pending',
                progress: p.progressPercent ?? 0,
                errorMessage: p.errorMessage ?? null
            }));

            for (const s of scenes) {
                if (s.status !== 'Done' && s.status !== 'Failed') {
                    await this.subscribeToProject(s.id);
                }
            }

            return scenes;
        } catch (e) {
            console.error('Failed to fetch projects:', e);
            return [];
        }
    }

    async uploadScene(name: string, files: File[]): Promise<boolean> {
        try {
            // Step 1: Create project
            const createRes = await this.apiFetch('/projects', {
                method: 'POST',
                body: JSON.stringify({ name })
            });
            if (!createRes.ok) throw new Error('Failed to create project');
            const project = await createRes.json();
            const projectId: string = project.id;

            // Step 2: Upload assets via XHR for progress tracking
            const uploaded = await new Promise<boolean>((resolve) => {
                const formData = new FormData();
                files.forEach(f => formData.append('files', f, f.name));

                const xhr = new XMLHttpRequest();
                xhr.open('POST', `${this.apiBaseUrl}/projects/${projectId}/assets`);
                xhr.setRequestHeader('Authorization', `Bearer ${this.accessToken}`);

                xhr.upload.addEventListener('progress', (e) => {
                    if (e.lengthComputable) {
                        this.events.fire('upload.progress', (e.loaded / e.total) * 100);
                    }
                });

                xhr.onload = () => resolve(xhr.status >= 200 && xhr.status < 300);
                xhr.onerror = () => resolve(false);
                xhr.send(formData);
            });

            if (!uploaded) throw new Error('Asset upload failed');

            // Step 3: Start Gaussian Splatting generation
            const startRes = await this.apiFetch(`/projects/${projectId}/start`, { method: 'POST' });
            // 409 means already processing/done — treat as non-fatal
            if (!startRes.ok && startRes.status !== 409) {
                throw new Error('Failed to start project processing');
            }

            await this.subscribeToProject(projectId);
            return true;
        } catch (error) {
            console.error('Upload error:', error);
            return false;
        }
    }

    async downloadScene(
        scene: RemoteScene,
        onProgress?: (loaded: number, total: number) => void,
        signal?: AbortSignal
    ): Promise<Blob | null> {
        try {
            const res = await this.apiFetch(`/projects/${scene.id}/download`, { signal });
            if (!res.ok) return null;

            // Absent behind a proxy that gzips or chunks the response; callers treat
            // total === 0 as "size unknown" and show an indeterminate byte count.
            const total = Number(res.headers.get('Content-Length') ?? 0);

            if (!onProgress || !res.body) {
                return await res.blob();
            }

            const reader = res.body.getReader();
            const chunks: Uint8Array[] = [];
            let loaded = 0;

            for (;;) {
                const { done, value } = await reader.read();
                if (done) break;
                chunks.push(value);
                loaded += value.length;
                onProgress(loaded, total);
            }

            return new Blob(chunks, {
                type: res.headers.get('Content-Type') ?? 'application/octet-stream'
            });
        } catch (error) {
            // Cancelling via the progress dialog aborts the fetch; not an error.
            if ((error as any)?.name === 'AbortError') return null;
            console.error('Download error:', error);
            return null;
        }
    }
}

export { AuthService };
